import React from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { useUpdateSubscriptionItemPricingOverride } from 'src/features/subscriptionItemPricingOverrides/api/useUpdateSubscriptionItemPricingOverride';
import { LIST_SUBSCRIPTION_ITEM_PRICING_OVERRIDES_RESOURCE } from 'src/features/subscriptionItemPricingOverrides/api/useListSubscriptionItemPricingOverrides';
import { LIST_SUBSCRIPTION_ITEMS_RESOURCE } from 'src/features/subscriptionItems/api/useListSubscriptionItems';
import { pricingOverridesMessages } from 'src/features/subscriptionItemPricingOverrides/utils/messages';
import SubscriptionItemPricingOverrideFormModal from 'src/features/subscriptionItemPricingOverrides/components/forms/SubscriptionItemPricingOverrideFormModal';
import { SubscriptionItemPricingOverride } from 'src/features/subscriptionItemPricingOverrides/utils/types';

interface UpdateSubscriptionItemPricingOverrideFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedPricingOverride: SubscriptionItemPricingOverride;
    children?: React.ReactNode;
}

function UpdateSubscriptionItemPricingOverrideFormModal<TData>({
    selectedPricingOverride,
    className,
    children,
}: UpdateSubscriptionItemPricingOverrideFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { subscriptionId = '', subscriptionItemId = '' } = useParams();

    const { mutate, isPending } = useUpdateSubscriptionItemPricingOverride({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEM_PRICING_OVERRIDES_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEMS_RESOURCE] });
            toast({
                title: formatMessage(pricingOverridesMessages.updatePricingOverrideSuccess),
                description: formatMessage(pricingOverridesMessages.updatePricingOverrideSuccessDescription),
            });
        },
    });

    return (
        <SubscriptionItemPricingOverrideFormModal
            className={className}
            selectedPricingOverride={selectedPricingOverride}
            mutate={(values, onSuccess, onError) =>
                mutate(
                    {
                        subscriptionId,
                        itemId: subscriptionItemId,
                        overrideId: selectedPricingOverride.id,
                        ...values,
                    },
                    { onSuccess, onError },
                )
            }
            title={formatMessage(generalMessages.editEntry, {
                entry: formatMessage(pricingOverridesMessages.pricingOverride),
            })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingOverridesMessages.editPricingOverrideSubtitle)}
        >
            {children}
        </SubscriptionItemPricingOverrideFormModal>
    );
}

export default UpdateSubscriptionItemPricingOverrideFormModal;
